import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { FileMatching, Activity } from './interfaces';
import { cleanFileName, getUnmatchedFiles } from './utils';
import Swal from 'sweetalert2';

interface Step4FileMatchingProps {
  fileMatching: FileMatching[];
  cardFiles: File[];
  activities: Activity[];
  projectName: string;
  onFileMatchingChange: (matching: FileMatching[]) => void;
  onCardFilesChange: (files: File[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step4FileMatching: React.FC<Step4FileMatchingProps> = ({
  fileMatching: initialFileMatching,
  cardFiles,
  activities,
  projectName,
  onFileMatchingChange,
  onCardFilesChange,
  onNext,
  onBack,
}) => {
  const [fileMatching, setFileMatching] = useState<FileMatching[]>(initialFileMatching);
  const [unmatchedFilesModalOpen, setUnmatchedFilesModalOpen] = useState(false);
  const [unmatchedFiles, setUnmatchedFiles] = useState<File[]>([]);
  const [selectedUnmatchedFile, setSelectedUnmatchedFile] = useState<File | null>(null);
  const [selectedActivityForUnmatched, setSelectedActivityForUnmatched] = useState<string | null>(null);

  useEffect(() => {
    const unmatched = getUnmatchedFiles(cardFiles, fileMatching);
    setUnmatchedFiles(unmatched);
  }, [cardFiles, fileMatching]);

  const handleMarkAsOk = (index: number) => {
    const newMatching = [...fileMatching];
    if (newMatching[index].status === 'error') {
      newMatching[index].status = 'matched';
    }
    setFileMatching(newMatching);
    onFileMatchingChange(newMatching);
  };

  const handleRemoveFile = (matchingIndex: number, fileToRemove: File) => {
    const newMatching = [...fileMatching];
    const matching = newMatching[matchingIndex];
    
    // Remover el archivo de la lista de archivos asociados
    matching.matchedFiles = matching.matchedFiles.filter(f => f !== fileToRemove);
    
    // Actualizar el estado según si quedan archivos
    if (matching.matchedFiles.length === 0) {
      matching.status = matching.activity.shouldHasCardsLoaded ? 'missing' : 'pending';
    }
    
    setFileMatching(newMatching);
    onFileMatchingChange(newMatching);
  };

  const handleOpenUnmatchedModal = () => {
    if (unmatchedFiles.length > 0) {
      setUnmatchedFilesModalOpen(true);
      setSelectedUnmatchedFile(unmatchedFiles[0]);
      setSelectedActivityForUnmatched(null);
    }
  };

  const handleAssignUnmatchedFile = (file: File, activityId: string) => {
    const newMatching = [...fileMatching];
    const matchingIndex = newMatching.findIndex(m => m.activityId === activityId);
    
    if (matchingIndex >= 0) {
      const matching = newMatching[matchingIndex];
      if (!matching.matchedFiles.includes(file)) {
        matching.matchedFiles.push(file);
        matching.status = 'matched';
      }
      
      setFileMatching(newMatching);
      onFileMatchingChange(newMatching);
      
      const newUnmatched = unmatchedFiles.filter(f => f !== file);
      setUnmatchedFiles(newUnmatched);
      
      if (newUnmatched.length === 0) {
        setUnmatchedFilesModalOpen(false);
      }
    }
  };

  const handleContinue = () => {
    const hasErrors = fileMatching.some(m => m.status === 'error');
    const hasMissing = fileMatching.some(m => m.status === 'missing' && m.activity.shouldHasCardsLoaded);
    
    if (hasErrors) {
      Swal.fire({
        title: 'Error',
        text: 'Debe resolver todos los errores antes de continuar',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (hasMissing) {
      Swal.fire({
        title: 'Advertencia',
        text: 'Hay actividades que requieren cartas pero no tienen archivo asociado. ¿Desea continuar de todas formas?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          if (unmatchedFiles.length > 0) {
            Swal.fire({
              title: 'Archivos no asociados',
              html: `<p>Los siguientes archivos .txt no se cargarán:</p><ul>${unmatchedFiles.map(f => `<li>${f.name}</li>`).join('')}</ul>`,
              icon: 'info',
              confirmButtonText: 'Continuar',
            }).then(() => {
              onNext();
            });
          } else {
            onNext();
          }
        }
      });
      return;
    }

    if (unmatchedFiles.length > 0) {
      Swal.fire({
        title: 'Archivos no asociados',
        html: `<p>Los siguientes archivos .txt no se cargarán:</p><ul>${unmatchedFiles.map(f => `<li>${f.name}</li>`).join('')}</ul>`,
        icon: 'info',
        confirmButtonText: 'Continuar',
      }).then(() => {
        onNext();
      });
    } else {
      onNext();
    }
  };

  const getStatusColor = (status: FileMatching['status']) => {
    switch (status) {
      case 'matched':
        return 'success';
      case 'error':
        return 'error';
      case 'missing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: FileMatching['status']) => {
    switch (status) {
      case 'matched':
        return 'OK';
      case 'error':
        return 'Error';
      case 'missing':
        return 'Falta archivo';
      default:
        return 'Pendiente';
    }
  };

  return (
    <>
      <Grid container className="dyn-cards-wizard-container" spacing={2}>
        <Paper className="dyn-cards-wizard-paper" elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography className="dyn-cards-wizard-title" sx={{ fontSize: 16, fontWeight: 600, mb: 2, flexShrink: 0 }}>
            PASO 4: VERIFICAR ASOCIACIÓN DE ARCHIVOS
          </Typography>

          {unmatchedFiles.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1, flexShrink: 0 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Hay {unmatchedFiles.length} archivo(s) .txt sin asociar
              </Typography>
              <Button variant="outlined" size="small" onClick={handleOpenUnmatchedModal}>
                Asociar Archivos
              </Button>
            </Box>
          )}

          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            minHeight: 0,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
          }}>
            <List sx={{ py: 0 }}>
              {fileMatching
                .filter(m => m.activity.shouldHasCardsLoaded)
                .map((matching, index) => {
                  const matchingIndex = fileMatching.indexOf(matching);
                  const cardCount = matching.matchedFiles.length;
                  return (
                    <ListItem
                      key={matching.activityId}
                      sx={{
                        bgcolor: matching.status === 'matched' 
                          ? 'rgba(76, 175, 80, 0.15)' // Verde con opacidad reducida
                          : matching.status === 'error' 
                          ? 'rgba(244, 67, 54, 0.15)' // Rojo con opacidad reducida
                          : 'background.paper',
                        mb: 1,
                        py: 0.1,
                        px: 1.5,
                        borderRadius: 1,
                        border: matching.status === 'error' ? '2px solid' : '1px solid',
                        borderColor: matching.status === 'error' ? 'error.main' : 'divider',
                      }}
                    >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
                            {matching.activity.locationName}
                          </Typography>
                          <Chip
                            label={getStatusText(matching.status)}
                            color={getStatusColor(matching.status) as any}
                            size="small"
                            sx={{ height: '22px', fontSize: '0.7rem' }}
                          />
                          {cardCount > 0 && (
                            <Chip
                              label={`${cardCount} carta${cardCount > 1 ? 's' : ''}`}
                              size="small"
                              sx={{ bgcolor: 'rgba(255, 183, 77, 0.3)', height: '22px', fontSize: '0.7rem' }} // Naranja con opacidad reducida
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.25 }}>
                          {matching.matchedFiles.length > 0 ? (
                            <Box>
                              {matching.matchedFiles.map((file, fileIndex) => (
                                <Box key={fileIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    {file.name}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(matchingIndex, file)}
                                    title="Desasociar archivo"
                                    sx={{ p: 0.25, width: '24px', height: '24px' }}
                                  >
                                    <DeleteIcon fontSize="small" sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              Sin archivos asociados
                            </Typography>
                          )}
                          {matching.status === 'error' && (
                            <Typography variant="caption" color="error" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              Esta actividad no requiere carta
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {matching.status === 'error' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleMarkAsOk(matchingIndex)}
                          title="Marcar como OK"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          <Grid container spacing={2} justifyContent="space-between" sx={{ mt: 'auto', flexShrink: 0 }}>
            <Button variant="outlined" onClick={onBack}>
              Atrás
            </Button>
            <Button variant="contained" onClick={handleContinue}>
              Continuar
            </Button>
          </Grid>
        </Paper>
      </Grid>

      {/* Modal para archivos no asociados */}
      <Dialog 
        open={unmatchedFilesModalOpen} 
        onClose={() => setUnmatchedFilesModalOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>Asociar Archivos a Pozos</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Lista de pozos disponibles (izquierda) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Pozos Disponibles
              </Typography>
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                maxHeight: '400px', 
                overflowY: 'auto' 
              }}>
                <List>
                  {activities
                    .filter(a => a.shouldHasCardsLoaded)
                    .map((activity) => {
                      const matching = fileMatching.find(m => m.activityId === activity.id);
                      const cardCount = matching?.matchedFiles.length || 0;
                      return (
                        <ListItem
                          key={activity.id}
                          onClick={() => setSelectedActivityForUnmatched(activity.id)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: selectedActivityForUnmatched === activity.id ? 'primary.light' : 'transparent',
                            '&:hover': {
                              bgcolor: selectedActivityForUnmatched === activity.id ? 'primary.light' : 'action.hover',
                            },
                          }}
                        >
                          <ListItemText
                            primary={activity.locationName}
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block">
                                  {activity.executedUser}
                                </Typography>
                                {cardCount > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    {cardCount} carta{cardCount > 1 ? 's' : ''} asociada{cardCount > 1 ? 's' : ''}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </Box>
            </Grid>

            {/* Lista de archivos pendientes (derecha) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Archivos Pendientes
              </Typography>
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                maxHeight: '400px', 
                overflowY: 'auto' 
              }}>
                {unmatchedFiles.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay archivos pendientes
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {unmatchedFiles.map((file) => (
                      <ListItem
                        key={file.name}
                        onClick={() => setSelectedUnmatchedFile(file)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: selectedUnmatchedFile === file ? 'primary.light' : 'transparent',
                          '&:hover': {
                            bgcolor: selectedUnmatchedFile === file ? 'primary.light' : 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(2)} KB`}
                        />
                        {selectedActivityForUnmatched && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignUnmatchedFile(file, selectedActivityForUnmatched);
                            }}
                            title="Asociar a pozo seleccionado"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmatchedFilesModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

